import React, { useState, useEffect, useRef, useCallback } from 'react';
import Cookies from 'js-cookie'; // Import js-cookie
import { generatorApi } from '../services/generatorService';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { FaCogs, FaChevronDown, FaFileAlt, FaSpinner, FaDownload, FaSearch, FaTimes, FaFilter, FaCode, FaCheckCircle, FaExclamationTriangle, FaEye, FaCopy } from 'react-icons/fa';

// Componente per il dropdown ricercabile con checkbox
const SearchableCheckboxDropdown = ({ 
  items, 
  selectedIds, 
  onSelectionChange, 
  placeholder, 
  getItemLabel, 
  getItemValue, 
  getItemKey,
  disabled = false,
  theme,
  maxHeight = "max-h-64"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  
  const filteredItems = items.filter(item => 
    getItemLabel(item).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleToggleAll = () => {
    if (selectedIds.length === filteredItems.length) {
      // Deselect all filtered items
      const remainingIds = selectedIds.filter(id => 
        !filteredItems.some(item => getItemValue(item) === id)
      );
      onSelectionChange(remainingIds);
    } else {
      // Select all filtered items
      const newIds = [...selectedIds];
      filteredItems.forEach(item => {
        const value = getItemValue(item);
        if (!newIds.includes(value)) {
          newIds.push(value);
        }
      });
      onSelectionChange(newIds);
    }
  };

  const handleItemToggle = (itemValue) => {
    if (selectedIds.includes(itemValue)) {
      onSelectionChange(selectedIds.filter(id => id !== itemValue));
    } else {
      onSelectionChange([...selectedIds, itemValue]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm(''); // Reset search when closing
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedCount = selectedIds.length;
  const filteredSelectedCount = filteredItems.filter(item => 
    selectedIds.includes(getItemValue(item))
  ).length;

  const dropdownClasses = theme === 'dark' 
    ? 'bg-gray-700 border-gray-600 text-gray-100' 
    : 'bg-white border-gray-300 text-gray-800';
  
  const itemHoverClasses = theme === 'dark' 
    ? 'hover:bg-gray-600' 
    : 'hover:bg-emerald-50';

  const checkboxClasses = theme === 'dark' 
    ? 'h-4 w-4 rounded border-gray-500 bg-gray-600 text-indigo-500 focus:ring-indigo-400'
    : 'h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500';

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Input field per la ricerca diretta */}
      <input
        type="text"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onFocus={() => setIsOpen(true)}
        placeholder={selectedCount === 0 ? placeholder : `${selectedCount} selezionati - digita per cercare...`}
        disabled={disabled}
        className={`w-full p-2.5 text-sm rounded-md transition-colors duration-300 border ${dropdownClasses} focus:ring-2 ${
          theme === 'dark' ? 'focus:ring-indigo-500 focus:border-indigo-500' : 'focus:ring-emerald-500 focus:border-emerald-500'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
      />

      {isOpen && (
        <div className={`absolute z-[200] w-full mt-1 ${dropdownClasses} border rounded-md shadow-lg max-h-64 overflow-hidden`}>
          {/* Select All Toggle - solo se ci sono risultati filtrati */}
          {filteredItems.length > 0 && (
            <div className={`px-3 py-2 border-b border-gray-300 dark:border-gray-600 ${itemHoverClasses}`}>
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filteredSelectedCount === filteredItems.length && filteredItems.length > 0}
                  onChange={handleToggleAll}
                  className={checkboxClasses}
                />
                <span className="text-sm font-medium">
                  {filteredSelectedCount === filteredItems.length ? 'Deseleziona tutto' : 'Seleziona tutto'}
                  {searchTerm && ` (${filteredItems.length} filtrati)`}
                </span>
              </label>
            </div>
          )}

          {/* Items List */}
          <div className={`${maxHeight} overflow-y-auto`}>
            {filteredItems.length > 0 ? (
              <div className="py-1">
                {filteredItems.map(item => {
                  const itemValue = getItemValue(item);
                  const isSelected = selectedIds.includes(itemValue);
                  return (
                    <label
                      key={getItemKey(item)}
                      className={`flex items-center space-x-3 px-3 py-2 cursor-pointer transition-colors ${itemHoverClasses} ${
                        isSelected ? (theme === 'dark' ? 'bg-indigo-900/30' : 'bg-emerald-100/50') : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleItemToggle(itemValue)}
                        className={checkboxClasses}
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm truncate block" title={getItemLabel(item)}>
                          {getItemLabel(item)}
                        </span>
                      </div>
                      {isSelected && (
                        <FaCheckCircle className={`h-4 w-4 ${theme === 'dark' ? 'text-indigo-400' : 'text-emerald-500'}`} />
                      )}
                    </label>
                  );
                })}
              </div>
            ) : (
              <div className="px-3 py-8 text-center text-gray-500 dark:text-gray-400">
                <FaSearch className="mx-auto h-8 w-8 mb-2 opacity-50" />
                <p className="text-sm">
                  {searchTerm ? `Nessun risultato per "${searchTerm}"` : 'Nessun elemento disponibile'}
                </p>
              </div>
            )}
          </div>

          {/* Footer con conteggio */}
          {items.length > 0 && (
            <div className="px-3 py-2 border-t border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-xs text-gray-600 dark:text-gray-400">
              {searchTerm ? (
                <>Mostrando {filteredItems.length} di {items.length} • {selectedCount} selezionati</>
              ) : (
                <>{items.length} elementi totali • {selectedCount} selezionati</>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Componente per le statistiche di stato
const StatusStats = ({ generationJobs, theme }) => {
  const successCount = generationJobs.filter(job => job.success).length;
  const errorCount = generationJobs.filter(job => !job.success).length;
  const totalCount = generationJobs.length;

  if (totalCount === 0) return null;

  const statClasses = theme === 'dark' ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200';
  const textClasses = theme === 'dark' ? 'text-gray-300' : 'text-gray-700';

  return (
    <div className={`grid grid-cols-3 gap-4 p-4 rounded-lg border ${statClasses} mb-4`}>
      <div className="text-center">
        <div className={`text-2xl font-bold ${successCount > 0 ? 'text-green-500' : textClasses}`}>
          {successCount}
        </div>
        <div className={`text-sm ${textClasses}`}>Successi</div>
      </div>
      <div className="text-center">
        <div className={`text-2xl font-bold ${errorCount > 0 ? 'text-red-500' : textClasses}`}>
          {errorCount}
        </div>
        <div className={`text-sm ${textClasses}`}>Errori</div>
      </div>
      <div className="text-center">
        <div className={`text-2xl font-bold ${theme === 'dark' ? 'text-indigo-400' : 'text-emerald-600'}`}>
          {totalCount}
        </div>
        <div className={`text-sm ${textClasses}`}>Totale</div>
      </div>
    </div>
  );
};

// Placeholder per componenti comuni
const Loader = () => (
  <div className="flex justify-center items-center p-6">
    <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-200 border-t-emerald-600 dark:border-gray-600 dark:border-t-indigo-500"></div>
    <div className="ml-4">
      <p className="text-emerald-700 dark:text-indigo-100 font-medium">Caricamento in corso...</p>
      <p className="text-emerald-600 dark:text-indigo-200 text-sm">Attendere prego</p>
    </div>
  </div>
);

const ErrorMessage = ({ message }) => (
  <div className="p-4 my-3 text-sm text-red-700 bg-red-50 rounded-lg border-l-4 border-red-500 dark:bg-red-900/20 dark:text-red-300 dark:border-red-600 flex items-start" role="alert">
    <FaExclamationTriangle className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0" />
    <div>
      <span className="font-semibold">Errore!</span>
      <div className="mt-1">{message}</div>
    </div>
  </div>
);

const SuccessMessage = ({ message }) => (
  <div className="p-4 my-3 text-sm text-green-700 bg-green-50 rounded-lg border-l-4 border-green-500 dark:bg-green-900/20 dark:text-green-300 dark:border-green-600 flex items-start" role="alert">
    <FaCheckCircle className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0" />
    <div>
      <span className="font-semibold">Successo!</span>
      <div className="mt-1">{message}</div>
    </div>
  </div>
);

const GeneratorePage = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const alarmAudioContextRef = useRef(null);
  const alarmOscillatorRef = useRef(null);

  const stopAlarm = useCallback(() => {
    if (alarmOscillatorRef.current) {
      try { alarmOscillatorRef.current.stop(); } catch (e) { /* already stopped */ }
      alarmOscillatorRef.current = null;
    }
    if (alarmAudioContextRef.current) {
      try { alarmAudioContextRef.current.close(); } catch (e) { /* already closed */ }
      alarmAudioContextRef.current = null;
    }
  }, []);

  const [skeletonRoots, setSkeletonRoots] = useState([]); // AGGIUNTO: Lista delle cartelle SKL
  const [selectedSkeletonRoot, setSelectedSkeletonRoot] = useState(''); // AGGIUNTO: Cartella selezionata
  const [loadingSkeletonRoots, setLoadingSkeletonRoots] = useState(false); // AGGIUNTO: Loading per roots
  const [loadingSkeletons, setLoadingSkeletons] = useState(false); // AGGIUNTO: Loading per scheletri

  const [skeletons, setSkeletons] = useState([]);
  const [products, setProducts] = useState([]);
  const [modules, setModules] = useState([]);
  const [allModules, setAllModules] = useState([]);

  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedSkeletonPath, setSelectedSkeletonPath] = useState('');
  const [selectedModuleIds, setSelectedModuleIds] = useState([]);
  
  // Debug flag with cookie
  const [isDebugEnabled, setIsDebugEnabled] = useState(() => {
    const cookieVal = Cookies.get('isDebugEnabled');
    return cookieVal === 'true';
  });

  // Auto ZIP download flag with cookie
  const [isAutoZipDownloadEnabled, setIsAutoZipDownloadEnabled] = useState(() => {
    const cookieVal = Cookies.get('isAutoZipDownloadEnabled');
    return cookieVal === 'true';
  });

  const [skeletonGuideTableNameForLabel, setSkeletonGuideTableNameForLabel] = useState('');
  const [skeletonGuideTableItems, setSkeletonGuideTableItems] = useState([]);
  const [selectedSkeletonGuideItemIds, setSelectedSkeletonGuideItemIds] = useState([]);
  const [fullGuideTableNameForPayload, setFullGuideTableNameForPayload] = useState('');
  const [idFieldForPayloadForContext, setIdFieldForPayloadForContext] = useState('');
  const [moduleFilterFieldNameForJar, setModuleFilterFieldNameForJar] = useState('TAB_Moduli');
  const [loadingContext, setLoadingContext] = useState(false);

  const [loading, setLoading] = useState(false);
  const [overallStatus, setOverallStatus] = useState({ type: '', message: '' });
  const [generationJobs, setGenerationJobs] = useState([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationModalMessage, setConfirmationModalMessage] = useState('');
  const [showZipModal, setShowZipModal] = useState(false);
  const [filesForZipModal, setFilesForZipModal] = useState([]);
  const [previewContent, setPreviewContent] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [expandedJobDetails, setExpandedJobDetails] = useState({});
  const [showSkeletonModal, setShowSkeletonModal] = useState(false);

  // Effect for isDebugEnabled cookie
  useEffect(() => {
    Cookies.set('isDebugEnabled', isDebugEnabled, { expires: 365 });
  }, [isDebugEnabled]);

  // Nuclear alarm when 20+ selections or none selected (= all)
  useEffect(() => {
    const numSelected = selectedSkeletonGuideItemIds.length;
    const shouldAlarm = showConfirmationModal
      && (numSelected === 0 || numSelected >= 20);
    if (shouldAlarm) {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        alarmAudioContextRef.current = ctx;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(440, ctx.currentTime);
        // Siren sweep: ramp up and down repeatedly
        const duration = 120; // max seconds
        for (let t = 0; t < duration; t += 2) {
          osc.frequency.linearRampToValueAtTime(880, ctx.currentTime + t + 1);
          osc.frequency.linearRampToValueAtTime(440, ctx.currentTime + t + 2);
        }
        gain.gain.setValueAtTime(0.3, ctx.currentTime);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        alarmOscillatorRef.current = osc;
      } catch (e) {
        console.warn('Could not start alarm audio:', e);
      }
    } else {
      stopAlarm();
    }
    return () => stopAlarm();
  }, [showConfirmationModal, stopAlarm, selectedSkeletonGuideItemIds]);

  // Effect for isAutoZipDownloadEnabled cookie
  useEffect(() => {
    Cookies.set('isAutoZipDownloadEnabled', isAutoZipDownloadEnabled, { expires: 365 });
  }, [isAutoZipDownloadEnabled]);

  // MODIFICATO: Carica risorse iniziali incluse le skeleton roots
  useEffect(() => {
    const loadInitialData = async () => {
      setLoadingSkeletonRoots(true);
      setLoading(true);
      setOverallStatus({ type: '', message: '' });
      try {
        const resources = await generatorApi.fetchResources();
        
        // Imposta skeleton roots
        const roots = resources.skeletonRoots || [];
        setSkeletonRoots(roots);
        
        // Imposta TIE come default se disponibile, altrimenti il primo
        if (roots.includes('TIE')) {
          setSelectedSkeletonRoot('TIE');
        } else if (roots.length > 0) {
          setSelectedSkeletonRoot(roots[0]);
        }
        
        // Format and sort products
        const formattedProducts = (resources.products || [])
          .map(p => ({
            ...p,
            displayLabel: `${p.code} - ${p.description}`
          }))
          .sort((a, b) => a.displayLabel.localeCompare(b.displayLabel));
        setProducts(formattedProducts);

        const initialModules = resources.modules || [];
        setModules(initialModules);
        setAllModules(initialModules);
      } catch (err) {
        setOverallStatus({ type: 'error', message: 'Errore nel caricamento delle risorse iniziali: ' + (err.response?.data?.message || err.message) });
      } finally {
        setLoadingSkeletonRoots(false);
        setLoading(false);
      }
    };
    loadInitialData();
  }, []);

  // AGGIUNTO: Carica gli scheletri quando cambia la root selezionata
  useEffect(() => {
    if (!selectedSkeletonRoot) {
      setSkeletons([]);
      setSelectedSkeletonPath('');
      return;
    }

    const loadSkeletonsForRoot = async () => {
      setLoadingSkeletons(true);
      setSelectedSkeletonPath(''); // Reset skeleton selection
      setSkeletonGuideTableItems([]); // Reset context items
      setSelectedSkeletonGuideItemIds([]); // Reset context selection
      try {
        const skeletonsData = await generatorApi.fetchSkeletonsByRoot(selectedSkeletonRoot);
        setSkeletons(skeletonsData || []);
      } catch (err) {
        console.error(`Errore nel caricamento degli scheletri per ${selectedSkeletonRoot}:`, err);
        setOverallStatus({ type: 'error', message: `Errore caricamento scheletri per ${selectedSkeletonRoot}: ${err.response?.data?.message || err.message}` });
        setSkeletons([]);
      } finally {
        setLoadingSkeletons(false);
      }
    };
    loadSkeletonsForRoot();
  }, [selectedSkeletonRoot]);

  useEffect(() => {
    if (selectedProductId) {
      const loadModulesForProduct = async () => {
        setLoading(true); 
        setOverallStatus({ type: '', message: '' });
        try {
          const productModules = await generatorApi.fetchModules(selectedProductId);
          setModules(productModules || []);
          if (productModules && productModules.length > 0) {
            setSelectedModuleIds(productModules.map(m => m.id));
          } else {
            setSelectedModuleIds([]); 
          }
        } catch (err) {
          setOverallStatus({ type: 'error', message: `Errore nel caricamento dei moduli per il prodotto: ` + (err.response?.data?.message || err.message) });
          setModules([]);
          setSelectedModuleIds([]); 
        } finally {
          setLoading(false);
        }
      };
      loadModulesForProduct();
    } else {
      setModules(allModules);
      setSelectedModuleIds([]); 
    }
  }, [selectedProductId, allModules]);

  // MODIFICATO: Aggiunto selectedSkeletonRoot come dipendenza
  useEffect(() => {
    const fetchContext = async () => {
      if (!selectedSkeletonPath || !selectedSkeletonRoot) { // AGGIUNTA CONDIZIONE selectedSkeletonRoot
        setSkeletonGuideTableNameForLabel('');
        setSkeletonGuideTableItems([]);
        setSelectedSkeletonGuideItemIds([]);
        setFullGuideTableNameForPayload('');
        setIdFieldForPayloadForContext('');
        setModuleFilterFieldNameForJar('TAB_Moduli'); 
        setLoadingContext(false);
        return;
      }

      setLoadingContext(true);
      setSelectedSkeletonGuideItemIds([]);
      setSkeletonGuideTableItems([]);
      setSkeletonGuideTableNameForLabel('');

      try {
        // MODIFICATO: Aggiunto selectedSkeletonRoot come primo parametro
        const response = await generatorApi.fetchSkeletonContextItems(selectedSkeletonRoot, selectedSkeletonPath, selectedProductId, selectedModuleIds);
        setSkeletonGuideTableNameForLabel(response.guideTableNameForLabel || '');
        setSkeletonGuideTableItems(response.guideTableItems || []);
        setFullGuideTableNameForPayload(response.fullGuideTableNameForPayload || '');
        setIdFieldForPayloadForContext(response.idFieldForPayload || '');
        setModuleFilterFieldNameForJar(response.moduleFilterFieldNameForJar || 'TAB_Moduli');
        if (!response.guideTableNameForLabel && (!response.guideTableItems || response.guideTableItems.length === 0)) {
            setSkeletonGuideTableNameForLabel('');
        }
      } catch (err) {
        setOverallStatus({ type: 'error', message: 'Errore nel caricamento del contesto dello scheletro: ' + (err.response?.data?.message || err.message) });
        setSkeletonGuideTableNameForLabel('');
        setSkeletonGuideTableItems([]);
        setFullGuideTableNameForPayload('');
        setIdFieldForPayloadForContext('');
        setModuleFilterFieldNameForJar('TAB_Moduli');
      } finally {
        setLoadingContext(false);
      }
    };

    fetchContext();
  }, [selectedSkeletonPath, selectedSkeletonRoot, selectedProductId, selectedModuleIds]); // AGGIUNTO selectedSkeletonRoot

  // AGGIUNTO: Handler per il cambio della skeleton root
  const handleSkeletonRootChange = (rootName) => {
    setSelectedSkeletonRoot(rootName);
    setSelectedSkeletonPath(''); // Reset skeleton selection
    setSkeletonGuideTableItems([]); // Reset context
    setSelectedSkeletonGuideItemIds([]);
    setGenerationJobs([]); // Reset previous jobs
    setOverallStatus({ type: '', message: '' });
  };

  const handleProductChange = (e) => {
    const newProductId = e.target.value;
    setSelectedProductId(newProductId);
    setSelectedSkeletonGuideItemIds([]);
  };

  // AGGIUNTO: Handler per selezione scheletro - RISOLVE ERRORE ESLINT
  const handleSkeletonSelection = (skeletonPath) => {
    setSelectedSkeletonPath(skeletonPath);
    setShowSkeletonModal(false);
    if (!skeletonPath) {
        setModuleFilterFieldNameForJar('TAB_Moduli');
    }
  };

  const handleModuleChange = (moduleId) => {
    setSelectedModuleIds(prev =>
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
    setSelectedSkeletonGuideItemIds([]);
  };

  const proceedWithGeneration = async () => {
    setShowConfirmationModal(false);
    setLoading(true);
    setOverallStatus({ type: '', message: '' });
    setGenerationJobs([]);
    setShowZipModal(false);
    setFilesForZipModal([]);
    setPreviewContent(null);

    const jsonPayloadArrayForModules = [];
    if (selectedModuleIds.length > 0) {
      const currentModuleFilterField = moduleFilterFieldNameForJar || "TAB_Moduli";
      jsonPayloadArrayForModules.push({
        Livello: 2,
        CampiChiave: [currentModuleFilterField, "IN", `(${selectedModuleIds.join(',')})`, currentModuleFilterField]
      });
    }

    const contextItemsToProcessIds = selectedSkeletonGuideItemIds;

    const payload = {
      scheletro: selectedSkeletonPath, // Path relativo alla root
      selectedSkeletonRoot: selectedSkeletonRoot, // AGGIUNTO: Root selezionata
      jsonPayload: jsonPayloadArrayForModules.length > 0 ? JSON.stringify(jsonPayloadArrayForModules) : null,
      logLevel: isDebugEnabled ? 'DEBUG' : null, // MODIFICATO: null se non debug
      contextItems: contextItemsToProcessIds.length > 0 ? {
        tableName: fullGuideTableNameForPayload,
        idField: idFieldForPayloadForContext,
        itemIds: contextItemsToProcessIds
      } : null
    };
    
    let calculatedChiaviForJar = 0;
    if (jsonPayloadArrayForModules.length > 0) calculatedChiaviForJar++;
    if (payload.contextItems) calculatedChiaviForJar++;
    payload.chiavi = calculatedChiaviForJar;

    try {
      // AGGIUNTO: Messaggio di status per esecuzione sequenziale
      if (contextItemsToProcessIds.length > 1) {
        setOverallStatus({ 
          type: 'info', 
          message: `Avvio esecuzione sequenziale di ${contextItemsToProcessIds.length} generazioni (con delay per evitare conflitti)...` 
        });
      }

      const results = await generatorApi.runGeneration(payload);
      console.log("Raw results from API:", JSON.stringify(results, null, 2));
      setLoading(false);

      if (!Array.isArray(results)) {
        console.error("API results are not an array:", results);
        setOverallStatus({ type: 'error', message: 'Risposta inattesa dal server (formato non array).' });
        setGenerationJobs([]);
        return;
      }

      // Update job list with all results
      setGenerationJobs(results.map(r => ({
        ...(r || {}),
        logContent: null,
        loadingLog: false,
        // Ensure generatedOutputFiles and generatedOutputFilePaths are arrays
        generatedOutputFiles: Array.isArray(r?.generatedOutputFiles) ? r.generatedOutputFiles : [],
        generatedOutputFilePaths: Array.isArray(r?.generatedOutputFilePaths) ? r.generatedOutputFilePaths : [],
        errorLog: r && !r.success ? (r.stderr || r.message || 'Errore task JAR.') : null
      })));
      
      const successfulJobsWithOutput = results.filter(
        r => r && r.success && r.generatedOutputFilePaths && r.generatedOutputFilePaths.length > 0
      );

      const allOutputFilesDetails = [];
      successfulJobsWithOutput.forEach(job => {
        job.generatedOutputFilePaths.forEach((filePath, index) => {
          allOutputFilesDetails.push({
            filePath: filePath,
            fileName: job.generatedOutputFiles[index] || `file_${index+1}`, // Fallback filename
            jobId: job.jobId,
            selected: true, // Default to selected for modal
          });
        });
      });

      setFilesForZipModal(allOutputFilesDetails);

      if (allOutputFilesDetails.length > 0) {
        if (isAutoZipDownloadEnabled) {
          setOverallStatus({ 
            type: 'success', 
            message: `Esecuzione sequenziale completata! Download ZIP con ${allOutputFilesDetails.length} file avviato...` 
          });
          setLoading(true);
          try {
            await generatorApi.downloadZip(allOutputFilesDetails.map(f => ({ filePath: f.filePath, fileName: f.fileName })));
            setShowZipModal(false);
            setPreviewContent(null);
          } catch (error) {
            setOverallStatus({ type: 'error', message: 'Errore durante il download automatico del ZIP: ' + (error.message || 'Errore sconosciuto') });
            if (allOutputFilesDetails.length > 1) setShowZipModal(true); // Show modal on auto-download error if multiple files
          }
          setLoading(false);
        } else {
          setOverallStatus({ 
            type: 'success', 
            message: `Esecuzione sequenziale completata! ${allOutputFilesDetails.length} file pronti. Seleziona per il download.` 
          });
          setShowZipModal(true);
        }
      } else if (results && results.length > 0) {
        const anySuccess = results.some(r => r.success);
        if (anySuccess) {
            setOverallStatus({ type: 'info', message: 'Esecuzione sequenziale completata, ma nessun file di output prodotto.' });
        } else {
            setOverallStatus({ type: 'error', message: 'Esecuzione sequenziale fallita o nessun file prodotto.' });
        }
      } else {
         setOverallStatus({ type: 'info', message: 'Nessun task di generazione eseguito o nessun risultato.' });
      }
      
      // Load logs for all jobs that ran (they have a jobId)
      results.forEach(job => {
        if (job && job.jobId) {
            setTimeout(() => handleFetchLog(job.jobId, job.logFilePath), 1000);
        }
      });

    } catch (err) {
      setLoading(false);
      setOverallStatus({ type: 'error', message: 'Errore durante la generazione: ' + (err.response?.data?.message || err.message) });
      setGenerationJobs([]);
    }
  };

  const handleGenerate = async () => {
    if (!selectedSkeletonPath) {
      setOverallStatus({ type: 'error', message: "Per favore, seleziona uno scheletro prima di generare." });
      return;
    }
    if (selectedModuleIds.length === 0) {
        setOverallStatus({ type: 'error', message: "Per favore, seleziona almeno un modulo." });
        return;
    }

    if (skeletonGuideTableItems.length > 0 && skeletonGuideTableNameForLabel) {
      const numSelectedContextItems = selectedSkeletonGuideItemIds.length;
      const totalContextItems = skeletonGuideTableItems.length;

      if (numSelectedContextItems === 0) {
        setConfirmationModalMessage(
          `Nessun ${skeletonGuideTableNameForLabel} specifico selezionato. La generazione procederà senza un filtro di contesto specifico per ${skeletonGuideTableNameForLabel} (potrebbe significare per tutti i ${totalContextItems} disponibili, o nessuno, a seconda dello scheletro). Continuare?`
        );
        setShowConfirmationModal(true);
        return;
      } else if (numSelectedContextItems === totalContextItems && totalContextItems > 1) { 
        // Conferma se tutti gli item sono selezionati (e ce n'è più di uno)
        setConfirmationModalMessage(
          `Hai selezionato tutti i ${totalContextItems} ${skeletonGuideTableNameForLabel} disponibili. Vuoi procedere con la generazione per tutti questi items?`
        );
        setShowConfirmationModal(true);
        return;
      } else if (numSelectedContextItems > 1 && numSelectedContextItems < totalContextItems) { 
        // Conferma per selezione multipla parziale (più di uno ma non tutti)
        setConfirmationModalMessage(
          `Hai selezionato ${numSelectedContextItems} ${skeletonGuideTableNameForLabel}. Vuoi procedere con la generazione per questi items?`
        );
        setShowConfirmationModal(true);
        return;
      }
      // Se 1 item selezionato, o se totalContextItems === 1 (e quindi numSelectedContextItems === 1), procede senza conferma.
    }
    await proceedWithGeneration(); 
  };
  
  const handleFetchLog = async (jobIdToFetch, logFilePathParam = null) => {
    if (!jobIdToFetch) return;

    const job = generationJobs.find(j => j.jobId === jobIdToFetch);
    // Use logFilePathParam if provided (e.g. from initial result), else use job.logFilePath (if fetched before)
    const logPathToUse = logFilePathParam || job?.logFilePath || null;

    setGenerationJobs(prevJobs =>
      prevJobs.map(j =>
        j.jobId === jobIdToFetch ? { ...j, loadingLog: true, errorLog: null } : j
      )
    );

    try {
      const logData = await generatorApi.fetchGenerationLog(jobIdToFetch, logPathToUse);
      setGenerationJobs(prevJobs =>
        prevJobs.map(j =>
          j.jobId === jobIdToFetch ? { ...j, logContent: logData, loadingLog: false, logFilePath: logPathToUse } : j // Store fetched logFilePath
        )
      );
    } catch (err) {
      const errMessage = err.response?.data?.message || err.message;
      let specificError = 'Errore nel caricamento del log: ' + errMessage;
      if (typeof err.response?.data === 'string' && err.response.data.includes("Log file not found")) {
          specificError = err.response.data;
      }
      setGenerationJobs(prevJobs =>
        prevJobs.map(j =>
          j.jobId === jobIdToFetch ? { ...j, errorLog: specificError, logContent: null, loadingLog: false } : j
        )
      );
    }
  };

  const handleZipFileSelectionChange = (index) => {
    setFilesForZipModal(prevFiles =>
      prevFiles.map((file, i) =>
        i === index ? { ...file, selected: !file.selected } : file
      )
    );
  };

  const handleSelectAllZipFiles = (selectAll) => {
      setFilesForZipModal(prevFiles =>
        prevFiles.map(file => ({ ...file, selected: selectAll }))
      );
  };

  const handlePreviewFile = async (file) => {
    setPreviewLoading(true);
    try {
      const data = await generatorApi.previewFile(file.filePath);
      setPreviewContent(data);
    } catch (err) {
      const errMsg = err.response?.data?.message || err.message || 'Errore sconosciuto';
      console.error('Preview error:', err.response?.status, errMsg, 'filePath:', file.filePath);
      setPreviewContent({ fileName: file.fileName, content: null, message: `Errore nel caricamento della preview: ${errMsg}` });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadZip = async () => {
    const selectedToDownload = filesForZipModal.filter(file => file.selected);
    if (selectedToDownload.length === 0) {
      setOverallStatus({ type: 'error', message: 'Nessun file selezionato per il download.' });
      return;
    }
    setLoading(true); // Use general loading indicator
    try {
      await generatorApi.downloadZip(selectedToDownload.map(f => ({ filePath: f.filePath, fileName: f.fileName })));
      setShowZipModal(false);
      setPreviewContent(null);
      setOverallStatus({ type: 'success', message: 'Download del file ZIP avviato.' });
    } catch (error) {
      setOverallStatus({ type: 'error', message: 'Errore durante il download del ZIP: ' + (error.message || 'Errore sconosciuto') });
    } finally {
      setLoading(false);
    }
  };

  // Handler to toggle job detail expansion
  const toggleJobDetails = (jobId) => {
    setExpandedJobDetails(prev => ({
      ...prev,
      [jobId]: !prev[jobId]
    }));
  };
  
  // Initialize expanded state when jobs are set - FIXED dependency array
  useEffect(() => {
    if (generationJobs.length > 0) {
      const initialExpansionState = {};
      generationJobs.forEach(job => {
        if (job.jobId) {
          // Expand successful jobs by default, collapse failed ones
          initialExpansionState[job.jobId] = job.success && job.generatedOutputFiles && job.generatedOutputFiles.length > 0; 
        }
      });
      setExpandedJobDetails(initialExpansionState);
    } else {
      setExpandedJobDetails({});
    }
  }, [generationJobs]); // FIXED: Simplified dependency array

  // Classi dinamiche per il tema
  const pageBg = theme === 'dark' ? 'bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950' : 'bg-gradient-to-br from-emerald-50 via-white to-emerald-100';
  const cardBg = theme === 'dark' ? 'bg-gray-800/80 backdrop-blur-sm' : 'bg-white/90 backdrop-blur-sm';
  const cardBorder = theme === 'dark' ? 'border-indigo-700/50' : 'border-emerald-400/50';
  const titleColor = theme === 'dark' ? 'text-indigo-100' : 'text-emerald-700';
  const labelColor = theme === 'dark' ? 'text-indigo-200' : 'text-emerald-700';
  const textColor = theme === 'dark' ? 'text-gray-300' : 'text-gray-700';
  const inputBaseClasses = `w-full p-3 text-sm rounded-lg transition-all duration-300`;
  const inputBorder = theme === 'dark' 
    ? 'border-gray-600 bg-gray-700/50 text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:bg-gray-700' 
    : 'border-gray-300 bg-white/80 text-gray-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white';
  const buttonPrimaryClasses = theme === 'dark' 
    ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white shadow-lg shadow-indigo-500/25' 
    : 'bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white shadow-lg shadow-emerald-500/25';
  const buttonSecondaryClasses = theme === 'dark'
    ? 'bg-gray-600 hover:bg-gray-500 text-indigo-100 border border-gray-500'
    : 'bg-gray-100 hover:bg-gray-200 text-emerald-700 border border-gray-300';
  const logViewerBg = theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50';
  const logViewerBorder = theme === 'dark' ? 'border-gray-700' : 'border-gray-300';
  const logViewerText = theme === 'dark' ? 'text-gray-300' : 'text-gray-800';

  // Add the missing class definitions for module selection
  const itemHoverClasses = theme === 'dark' 
    ? 'hover:bg-gray-600' 
    : 'hover:bg-emerald-50';

  const checkboxClasses = theme === 'dark' 
    ? 'h-4 w-4 rounded border-gray-500 bg-gray-600 text-indigo-500 focus:ring-indigo-400'
    : 'h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500';

  // Process skeletons into categories for better organization
  const organizeSkeletons = (skeletons) => {
    const categories = {};

    skeletons.forEach(skeleton => {
      const pathParts = skeleton.path.split('/');
      const category = pathParts[0];
      const fileName = pathParts[pathParts.length - 1];
      
      const processedSkeleton = {
        ...skeleton,
        fileName: fileName,
        category: category
      };

      if (!categories[category]) {
        categories[category] = {
          name: category,
          skeletons: []
        };
      }
      
      categories[category].skeletons.push(processedSkeleton);
    });

    Object.keys(categories).forEach(key => {
      categories[key].skeletons.sort((a, b) => a.fileName.localeCompare(b.fileName));
    });

    return categories;
  };

  const skeletonCategories = organizeSkeletons(skeletons);

  // Filtro MILO: se il prodotto selezionato è MILO, mostra solo "Oggetti TypeScript"
  const selectedProduct = products.find(p => String(p.id) === String(selectedProductId));
  const isMiloProduct = selectedProduct && (
    selectedProduct.code?.toUpperCase().includes('MILO') ||
    selectedProduct.description?.toUpperCase().includes('MILO')
  );
  const filteredSkeletonCategories = isMiloProduct
    ? Object.fromEntries(
        Object.entries(skeletonCategories).filter(([key]) =>
          key.toLowerCase().includes('typescript')
        )
      )
    : skeletonCategories;

  if (loadingSkeletonRoots) {
    return (
      <div className={`min-h-screen ${pageBg} transition-all duration-500 py-4 px-4 flex items-center justify-center`}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${pageBg} transition-all duration-500 py-4 px-4`}>
      {/* Full-width responsive container */}
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className={`${cardBg} border-2 ${cardBorder} rounded-xl shadow-lg px-4 py-2.5 mb-6 flex items-center justify-between`}>
          <div className="flex items-center">
            <div className={`p-1.5 rounded-lg ${theme === 'dark' ? 'bg-indigo-600' : 'bg-emerald-600'} mr-3`}>
              <FaCode className="h-4 w-4 text-white" />
            </div>
            <h1 className={`text-lg font-bold ${titleColor}`}>
              Generatore SiSketron
            </h1>
          </div>
          <p className={`${textColor} text-sm hidden sm:block`}>
            Configura i parametri e avvia la generazione
          </p>
        </div>

        {/* Main Content Grid - Responsive Layout */}
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          {/* Configuration Panel - Takes more space on desktop */}
          <div className="xl:col-span-3 space-y-6">
            <div className={`${cardBg} border-2 ${cardBorder} rounded-xl shadow-xl p-6`}>
              <div className="flex items-center mb-6">
                <FaFilter className={`h-5 w-5 ${titleColor} mr-2`} />
                <h2 className={`text-xl font-semibold ${titleColor}`}>Configurazione</h2>
              </div>

              {loading && <Loader />}
              {overallStatus.message && (
                overallStatus.type === 'error' ? 
                  <ErrorMessage message={overallStatus.message} /> : 
                  <SuccessMessage message={overallStatus.message} />
              )}

              {/* Configuration Grid - Better use of horizontal space */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Primary Selections */}
                <div className="space-y-6">
                  {/* Product Selection */}
                  <div>
                    <label htmlFor="product-select" className={`block text-sm font-semibold mb-2 ${labelColor}`}>
                      Seleziona Prodotto
                    </label>
                    <div className="relative">
                      <select 
                        id="product-select" 
                        value={selectedProductId} 
                        onChange={handleProductChange}
                        className={`${inputBaseClasses} border ${inputBorder} appearance-none`}
                        disabled={loading || loadingContext}
                      >
                        <option value="">-- Seleziona un Prodotto --</option>
                        {products.map(p => (
                          <option key={p.id} value={p.id}>{p.displayLabel}</option>
                        ))}
                      </select>
                      <FaChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 ${textColor} pointer-events-none`} />
                    </div>
                  </div>

                  {/* AGGIUNTO: Skeleton Root Selection con checkbox radio-style */}
                  {skeletonRoots.length > 0 && (
                    <div>
                      <label className={`block text-sm font-semibold mb-3 ${labelColor}`}>
                        Gruppo Scheletri
                      </label>
                      <div className="space-y-2">
                        {skeletonRoots.map(root => (
                          <label 
                            key={root}
                            className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all duration-200 ${
                              selectedSkeletonRoot === root
                                ? (theme === 'dark' ? 'bg-indigo-900/40 border-indigo-600/50' : 'bg-emerald-100/60 border-emerald-300/50')
                                : (theme === 'dark' ? 'bg-gray-700/30 border-gray-600/50 hover:bg-gray-600/50' : 'bg-gray-50 border-gray-200/50 hover:bg-gray-100')
                            }`}
                          >
                            <input
                              type="radio"
                              name="skeletonRoot"
                              value={root}
                              checked={selectedSkeletonRoot === root}
                              onChange={() => handleSkeletonRootChange(root)}
                              className={`h-4 w-4 border-gray-300 ${theme === 'dark' ? 'bg-gray-600 border-gray-500 text-indigo-500 focus:ring-indigo-400' : 'text-emerald-600 focus:ring-emerald-500'} mr-3`}
                              disabled={loadingSkeletons}
                            />
                            <div className="flex-1">
                              <span className={`font-medium ${textColor}`}>{root}</span>
                              <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                {loadingSkeletons && selectedSkeletonRoot === root ? 'Caricamento scheletri...' : 
                                 selectedSkeletonRoot === root ? `${skeletons.length} scheletri disponibili` : 
                                 'Gruppo di scheletri'}
                              </div>
                            </div>
                            {selectedSkeletonRoot === root && (
                              <FaCheckCircle className={`h-4 w-4 ${theme === 'dark' ? 'text-indigo-400' : 'text-emerald-500'}`} />
                            )}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Skeleton Selection */}
                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${labelColor}`}>
                      Seleziona Scheletro di Generazione
                      {selectedSkeletonRoot && (
                        <span className={`ml-2 text-xs ${theme === 'dark' ? 'text-indigo-400' : 'text-emerald-600'}`}>
                          ({selectedSkeletonRoot})
                        </span>
                      )}
                    </label>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowSkeletonModal(true)}
                        className={`flex-1 p-3 text-sm rounded-lg border ${inputBorder} text-left transition-all duration-200 hover:scale-[1.01] focus:ring-2 ${
                          theme === 'dark' ? 'focus:ring-indigo-500' : 'focus:ring-emerald-500'
                        }`}
                        disabled={loading || loadingSkeletons || !selectedSkeletonRoot}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            {selectedSkeletonPath ? (
                              <div>
                                <div className={`font-medium ${textColor} truncate`}>
                                  {skeletons.find(s => s.path === selectedSkeletonPath)?.path.split('/').pop() || 'Scheletro selezionato'}
                                </div>
                                <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} truncate`}>
                                  {selectedSkeletonPath}
                                </div>
                              </div>
                            ) : (
                              <span className={`${textColor} ${!selectedSkeletonRoot || loadingSkeletons ? 'opacity-50' : 'opacity-75'}`}>
                                {!selectedSkeletonRoot ? 'Seleziona prima un gruppo...' :
                                 loadingSkeletons ? 'Caricamento scheletri...' :
                                 'Clicca per selezionare uno scheletro...'}
                              </span>
                            )}
                          </div>
                          <FaChevronDown className={`h-4 w-4 ${textColor} ml-2 flex-shrink-0`} />
                        </div>
                      </button>
                      
                      {selectedSkeletonPath && (
                        <button
                          onClick={() => handleSkeletonSelection('')}
                          disabled={loading}
                          className={`px-3 py-2 rounded-lg ${buttonSecondaryClasses} transition-all duration-200 hover:scale-105 flex items-center`}
                          title="Deseleziona scheletro"
                        >
                          <FaTimes className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Options Section */}
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-600 space-y-3">
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="debug-checkbox" 
                        checked={isDebugEnabled} 
                        onChange={(e) => setIsDebugEnabled(e.target.checked)}
                        className={`h-4 w-4 rounded border-gray-300 ${theme === 'dark' ? 'bg-gray-600 border-gray-500 text-indigo-500 focus:ring-indigo-400' : 'text-emerald-600 focus:ring-emerald-500'} mr-3`}
                        disabled={loading}
                      />
                      <label htmlFor="debug-checkbox" className={`text-sm ${textColor} cursor-pointer`}>
                        Abilita Log di Debug (logLevel=DEBUG)
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="auto-zip-download-checkbox" 
                        checked={isAutoZipDownloadEnabled} 
                        onChange={(e) => setIsAutoZipDownloadEnabled(e.target.checked)}
                        className={`h-4 w-4 rounded border-gray-300 ${theme === 'dark' ? 'bg-gray-600 border-gray-500 text-indigo-500 focus:ring-indigo-400' : 'text-emerald-600 focus:ring-emerald-500'} mr-3`}
                        disabled={loading}
                      />
                      <label htmlFor="auto-zip-download-checkbox" className={`text-sm ${textColor} cursor-pointer`}>
                        Abilita Download ZIP Automatico
                      </label>
                    </div>
                  </div>
                </div>

                {/* Right Column - Module and Context Selections */}
                <div className="space-y-6">
                  {/* Module Selection */}
                  {selectedProductId && (
                    <div>
                      <label className={`block text-sm font-semibold mb-2 ${labelColor}`}>
                        Moduli per {products.find(p => p.id === selectedProductId)?.code || 'Prodotto'}
                      </label>
                      <div>
                        {modules.length > 0 ? (
                          <div className={`border rounded-lg ${inputBorder} ${theme === 'dark' ? 'bg-gray-700/30' : 'bg-white/50'}`}>
                            <div className={`px-3 py-2 border-b ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'} ${itemHoverClasses}`}>
                              <label className="flex items-center space-x-3 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={modules.length > 0 && modules.every(m => selectedModuleIds.includes(m.id))}
                                  onChange={() => {
                                    if (modules.length === 0) return;
                                    const allCurrentModuleIds = modules.map(m => m.id);
                                    const allCurrentlySelected = allCurrentModuleIds.length > 0 && allCurrentModuleIds.every(id => selectedModuleIds.includes(id));

                                    if (allCurrentlySelected) {
                                      setSelectedModuleIds(prev => prev.filter(id => !allCurrentModuleIds.includes(id)));
                                    } else {
                                      setSelectedModuleIds(prev => {
                                        const newSelection = [...prev];
                                        allCurrentModuleIds.forEach(id => {
                                          if (!newSelection.includes(id)) {
                                            newSelection.push(id);
                                          }
                                        });
                                        return newSelection;
                                      });
                                    }
                                    setSelectedSkeletonGuideItemIds([]);
                                  }}
                                  className={checkboxClasses}
                                  disabled={loading}
                                />
                                <span className="text-sm font-medium">
                                  {modules.every(m => selectedModuleIds.includes(m.id)) ? 'Deseleziona tutti' : 'Seleziona tutti'}
                                  &nbsp;({modules.length})
                                </span>
                              </label>
                            </div>
                            <div className="max-h-40 overflow-y-auto p-1">
                              {modules.map(module => {
                                const isSelected = selectedModuleIds.includes(module.id);
                                return (
                                  <label
                                    key={module.id}
                                    className={`flex items-center space-x-3 px-3 py-2 cursor-pointer rounded-md transition-colors ${itemHoverClasses} ${
                                      isSelected ? (theme === 'dark' ? 'bg-indigo-900/40' : 'bg-emerald-100/60') : ''
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => handleModuleChange(module.id)}
                                      className={checkboxClasses}
                                      disabled={loading}
                                    />
                                    <div className="flex-1 min-w-0">
                                      <span className="text-xs truncate block" title={`${module.description} (${module.code})`}>
                                        {module.description} ({module.code})
                                      </span>
                                    </div>
                                    {isSelected && (
                                      <FaCheckCircle className={`h-3 w-3 ${theme === 'dark' ? 'text-indigo-400' : 'text-emerald-500'}`} />
                                    )}
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          <div className={`p-4 border rounded-lg ${inputBorder} ${theme === 'dark' ? 'bg-gray-700/30' : 'bg-gray-50'} text-center`}>
                            <FaSearch className={`mx-auto h-6 w-6 mb-2 ${textColor} opacity-50`} />
                            <p className={`${textColor} text-xs`}>
                              Nessun modulo disponibile per il prodotto selezionato.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {!selectedProductId && (
                     <div className={`p-4 border rounded-lg ${inputBorder} ${theme === 'dark' ? 'bg-gray-700/30' : 'bg-gray-50'} text-center`}>
                          <p className={`${textColor} text-sm italic`}>
                            Seleziona un prodotto per visualizzare i moduli.
                          </p>
                      </div>
                  )}

                  {/* Context Item Selection */}
                  {selectedSkeletonPath && (loadingContext || skeletonGuideTableNameForLabel) && (
                    <div className="relative z-50">
                      <label className={`block text-sm font-semibold mb-2 ${labelColor}`}>
                        {loadingContext ? 'Caricamento Contesto...' : `${skeletonGuideTableNameForLabel || 'Item di Contesto'}`}
                        {selectedModuleIds.length === 0 && !loadingContext && skeletonGuideTableNameForLabel && 
                          <span className="text-xs text-red-500 dark:text-red-400 ml-2">(Richiede Moduli)</span>
                        }
                      </label>
                      {loadingContext && <Loader />}
                      {!loadingContext && skeletonGuideTableNameForLabel && skeletonGuideTableItems.length > 0 && (
                        <SearchableCheckboxDropdown
                          items={skeletonGuideTableItems}
                          selectedIds={selectedSkeletonGuideItemIds}
                          onSelectionChange={setSelectedSkeletonGuideItemIds}
                          placeholder={`Seleziona ${skeletonGuideTableNameForLabel}...`}
                          getItemLabel={(item) => `${item.name} (${item.id})`}
                          getItemValue={(item) => item.id}
                          getItemKey={(item) => item.id}
                          disabled={loading || (selectedModuleIds.length === 0 && modules.length > 0)}
                          theme={theme}
                          maxHeight="max-h-48"
                        />
                      )}
                      {!loadingContext && skeletonGuideTableNameForLabel && skeletonGuideTableItems.length === 0 && (
                         <div className={`p-3 border rounded-lg ${inputBorder} ${theme === 'dark' ? 'bg-gray-700/30' : 'bg-gray-50'} text-center`}>
                          <FaSearch className={`mx-auto h-6 w-6 mb-2 ${textColor} opacity-50`} />
                          <p className={`${textColor} text-xs`}>
                            Nessun {skeletonGuideTableNameForLabel} trovato.
                          </p>
                        </div>
                      )}
                      {!loadingContext && !skeletonGuideTableNameForLabel && selectedSkeletonPath && (
                        <div className={`p-3 border rounded-lg ${inputBorder} ${theme === 'dark' ? 'bg-gray-700/30' : 'bg-gray-50'} text-center`}>
                          <p className={`${textColor} text-xs italic`}>
                            Nessun item di contesto per questo scheletro.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Generate Button - Full Width */}
              <button
                onClick={handleGenerate}
                disabled={loading || !selectedSkeletonPath || selectedModuleIds.length === 0 || !selectedSkeletonRoot}
                className={`w-full mt-6 flex items-center justify-center ${inputBaseClasses} font-bold rounded-xl py-4 px-6 ${buttonPrimaryClasses} disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 ease-in-out focus:outline-none focus:ring-4 ${theme === 'dark' ? 'focus:ring-indigo-500/50' : 'focus:ring-emerald-500/50'} transform hover:scale-[1.01] active:scale-[0.99]`}
              >
                <FaCogs className="mr-3 h-6 w-6" />
                <span className="text-lg">
                  {loading ? 'Generazione in corso...' : 'Avvia Generazione (S.G.A.S.A)'}
                </span>
                {loading && <FaSpinner className="ml-3 h-5 w-5 animate-spin" />}
              </button>
            </div>
          </div>

          {/* Sidebar - Stats and Current Selections */}
          <div className="xl:col-span-1 space-y-6">
            {/* Statistics */}
            {generationJobs.length > 0 && (
              <div className={`${cardBg} border-2 ${cardBorder} rounded-xl shadow-xl p-4`}>
                <h3 className={`text-lg font-semibold ${titleColor} mb-3`}>Statistiche</h3>
                <StatusStats generationJobs={generationJobs} theme={theme} />
              </div>
            )}

            {/* Current Selections - Compact View */}
            <div className={`${cardBg} border-2 ${cardBorder} rounded-xl shadow-xl p-4`}>
              <h3 className={`text-lg font-semibold ${titleColor} mb-3`}>Selezioni</h3>
              <div className="space-y-3">
                {/* AGGIUNTO: Skeleton Root */}
                <div>
                  <span className={`text-xs font-medium ${labelColor} block mb-1`}>Gruppo:</span>
                  <div className={`text-xs ${textColor} p-2 rounded-md ${theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                    {selectedSkeletonRoot || <span className="italic text-gray-500">Nessuno</span>}
                  </div>
                </div>

                {/* Product */}
                <div>
                  <span className={`text-xs font-medium ${labelColor} block mb-1`}>Prodotto:</span>
                  <div className={`text-xs ${textColor} p-2 rounded-md ${theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                    {(() => {
                      if (!selectedProductId) {
                        return <span className="italic text-gray-500">Nessuno</span>;
                      }
                      const selectedProduct = products.find(p => p.id === parseInt(selectedProductId));
                      if (selectedProduct) {
                        return selectedProduct.displayLabel || selectedProduct.description || selectedProduct.code || 'Prodotto';
                      }
                      return 'Prodotto sconosciuto';
                    })()}
                  </div>
                </div>

                {/* Modules - Compact */}
                <div>
                  <span className={`text-xs font-medium ${labelColor} block mb-1`}>
                    Moduli ({selectedModuleIds.length}):
                  </span>
                  <div className={`text-xs ${textColor} p-2 rounded-md ${theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'} max-h-20 overflow-y-auto`}>
                    {selectedModuleIds.length > 0 ? (
                      <div className="space-y-1">
                        {modules
                          .filter(m => selectedModuleIds.includes(m.id))
                          .map(module => (
                            <div key={module.id} className={`text-xs px-1 py-0.5 rounded ${theme === 'dark' ? 'bg-gray-600/50' : 'bg-white'}`}>
                              {module.code}
                            </div>
                          ))
                        }
                      </div>
                    ) : (
                      <span className="italic text-gray-500">Nessuno</span>
                    )}
                  </div>
                </div>

                {/* Skeleton */}
                <div>
                  <span className={`text-xs font-medium ${labelColor} block mb-1`}>Scheletro:</span>
                  <div className={`text-xs ${textColor} p-2 rounded-md ${theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                    {selectedSkeletonPath ? (
                      <div>
                        <div className="font-medium truncate">
                          {skeletons.find(s => s.path === selectedSkeletonPath)?.path.split('/').pop() || 'Sconosciuto'}
                        </div>
                      </div>
                    ) : (
                      <span className="italic text-gray-500">Nessuno</span>
                    )}
                  </div>
                </div>

                {/* Context Items */}
                {skeletonGuideTableNameForLabel && (
                  <div>
                    <span className={`text-xs font-medium ${labelColor} block mb-1`}>
                      {skeletonGuideTableNameForLabel} ({selectedSkeletonGuideItemIds.length}):
                    </span>
                    <div className={`text-xs ${textColor} p-2 rounded-md ${theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'} max-h-16 overflow-y-auto`}>
                      {selectedSkeletonGuideItemIds.length > 0 ? (
                        <div className="space-y-1">
                          {skeletonGuideTableItems
                            .filter(item => selectedSkeletonGuideItemIds.includes(item.id))
                            .map(item => (
                              <div key={item.id} className={`text-xs px-1 py-0.5 rounded ${theme === 'dark' ? 'bg-gray-600/50' : 'bg-white'}`}>
                                {item.name}
                              </div>
                            ))
                          }
                        </div>
                      ) : (
                        <span className="italic text-gray-500">Nessuno</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Generation Results - Full Width with Adaptive Layout */}
          {generationJobs.length > 0 && !loading && (
            <div className={`mt-6 ${cardBg} border-2 ${cardBorder} rounded-xl shadow-xl p-6 relative z-0`}>
              <h2 className={`text-xl font-semibold ${titleColor} mb-6`}>Risultati Generazione</h2>
              
              {/* Adaptive layout - full width for readability */}
              <div className={`${
                generationJobs.length === 1 
                  ? 'w-full' 
                  : 'grid grid-cols-1 gap-5'
              }`}>
                {generationJobs.map((job, index) => (
                  <div 
                    key={job.jobId || index} 
                    className={`border-2 rounded-xl transition-all duration-300 overflow-hidden ${
                      generationJobs.length === 1 ? 'max-w-none' : ''
                    } ${
                      job.success && !job.errorLog ? 
                        (theme === 'dark' ? 'border-green-700/50 bg-green-900/20' : 'border-green-400/50 bg-green-50/50') :
                        (theme === 'dark' ? 'border-red-700/50 bg-red-900/20' : 'border-red-400/50 bg-red-50/50')
                    }`}
                  >
                    <div 
                      className={`p-4 cursor-pointer flex items-start justify-between ${
                        generationJobs.length === 1 ? 'lg:p-6' : ''
                      }`}
                      onClick={() => job.jobId && toggleJobDetails(job.jobId)}
                    >
                      <div className="flex-1">
                        <h4 className={`${generationJobs.length === 1 ? 'text-lg' : 'text-base'} font-semibold ${textColor} mb-2`}>
                          Task {index + 1}
                          {job.jobId && (
                           <FaChevronDown className={`inline-block ml-2 ${generationJobs.length === 1 ? 'h-4 w-4' : 'h-3 w-3'} transition-transform duration-200 ${expandedJobDetails[job.jobId] ? 'rotate-180' : ''}`} />
                        )}
                        </h4>
                        <p className={`${generationJobs.length === 1 ? 'text-sm' : 'text-sm'} ${textColor} opacity-75 mb-2`}>
                          {job.jobId || 'N/A'}
                        </p>
                        
                        {/* Show more details for single result */}
                        {generationJobs.length === 1 && job.statusMessage && (
                          <p className={`text-sm ${textColor} opacity-90 mb-3`}>
                            {job.statusMessage}
                          </p>
                        )}

                        {/* Show quick stats for single result */}
                        {generationJobs.length === 1 && job.success && job.generatedOutputFiles && (
                          <div className={`flex items-center gap-4 text-sm ${textColor} mb-3`}>
                            <div className="flex items-center">
                              <FaFileAlt className="h-4 w-4 mr-1 opacity-60" />
                              <span>{job.generatedOutputFiles.length} file{job.generatedOutputFiles.length !== 1 ? 's' : ''} generati</span>
                            </div>
                            {job.commandString && (
                              <div className="flex items-center">
                                <FaCogs className="h-4 w-4 mr-1 opacity-60" />
                                <span>Comando eseguito</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className={`px-3 py-1.5 rounded-full ${generationJobs.length === 1 ? 'text-sm px-4 py-2' : 'text-sm'} font-medium self-start ${
                        job.success ? 
                          'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                          'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                      }`}>
                        {job.success ? '✓ Successo' : '✗ Errore'}
                      </div>
                    </div>

                    {/* Enhanced action buttons area for single result */}
                    {generationJobs.length === 1 && (
                      <div className={`px-6 py-4 border-t border-gray-200 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-800/30`}>
                        <div className="flex flex-wrap gap-3">

                          {job.success && job.generatedOutputFiles && job.generatedOutputFiles.length > 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const filesForThisJob = job.generatedOutputFilePaths.map((fp, i) => ({
                                  filePath: fp,
                                  fileName: job.generatedOutputFiles[i] || `task_${index+1}_file_${i+1}`,
                                }));
                                setLoading(true);
                                generatorApi.downloadZip(filesForThisJob)
                                  .then(() => setOverallStatus({ type: 'success', message: `Download ZIP per Task ${index + 1} avviato.` }))
                                  .catch(error => setOverallStatus({ type: 'error', message: `Errore download ZIP Task ${index + 1}: ${error.message || 'Errore sconosciuto'}` }))
                                  .finally(() => setLoading(false));
                            }}
                            className={`flex items-center text-sm px-4 py-2 font-medium rounded-lg ${buttonPrimaryClasses} transition-all duration-200 hover:scale-105`}
                            disabled={loading}
                          >
                            <FaDownload className="mr-2 h-4 w-4" />
                            Scarica ZIP ({job.generatedOutputFiles.length} file{job.generatedOutputFiles.length > 1 ? 's' : ''})
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Show more details for single result */}
                  {generationJobs.length === 1 && (job.commandOutput || job.logFileContent) && (
                    <div className="px-6 py-4">
                      {/* Comando Eseguito */}
                      {job.commandString && (
                        <details className="mb-4">
                          <summary className={`cursor-pointer ${labelColor} text-sm font-medium hover:underline`}>
                            Comando Eseguito
                          </summary>
                          <pre className={`mt-2 p-4 border ${logViewerBorder} rounded-lg text-sm leading-relaxed ${logViewerBg} ${logViewerText} max-h-64 overflow-auto whitespace-pre-wrap break-words font-mono`}>
                            {job.commandString}
                          </pre>
                        </details>
                      )}
                      
                      {/* Output del Comando */}
                      {job.commandOutput && (
                        <details open className="mb-4">
                          <summary className={`cursor-pointer ${labelColor} text-sm font-medium hover:underline`}>
                            Output del Comando
                          </summary>
                          <pre className={`mt-2 p-4 border ${logViewerBorder} rounded-lg text-sm leading-relaxed ${logViewerBg} ${logViewerText} max-h-[500px] overflow-auto whitespace-pre-wrap break-words font-mono`}>
                            {job.commandOutput}
                          </pre>
                        </details>
                      )}

                      {/* NUOVO: Contenuto del File di Log */}
                      {job.logFileContent && (
                        <details open className="mb-4">
                          <summary className={`cursor-pointer ${labelColor} text-sm font-medium hover:underline`}>
                            Contenuto del File di Log
                          </summary>
                          <pre className={`mt-2 p-4 border ${logViewerBorder} rounded-lg text-sm leading-relaxed ${logViewerBg} ${logViewerText} max-h-[500px] overflow-auto whitespace-pre-wrap break-words font-mono`}>
                            {job.logFileContent}
                          </pre>
                        </details>
                      )}
                    </div>
                  )}
                  
                  {/* Collapsible Content */}
                  {job.jobId && expandedJobDetails[job.jobId] && (
                    <div className={`px-4 pb-4 border-t border-gray-300 dark:border-gray-700/50 ${
                      generationJobs.length === 1 ? 'lg:px-6' : ''
                    }`}>
                      {/* Show status message only if not shown above */}
                      {generationJobs.length > 1 && (job.statusMessage || job.message) && (
                        <p className={`${textColor} text-sm my-3`}>
                          {job.statusMessage || job.message}
                        </p>
                      )}

                      {/* Compact action buttons for multiple results */}
                      {generationJobs.length > 1 && (
                        <div className="flex flex-wrap gap-3 mt-3">

                          {job.success && job.generatedOutputFiles && job.generatedOutputFiles.length > 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const filesForThisJob = job.generatedOutputFilePaths.map((fp, i) => ({
                                  filePath: fp,
                                  fileName: job.generatedOutputFiles[i] || `task_${index+1}_file_${i+1}`,
                                }));
                                setLoading(true);
                                generatorApi.downloadZip(filesForThisJob)
                                  .then(() => setOverallStatus({ type: 'success', message: `Download ZIP per Task ${index + 1} avviato.` }))
                                  .catch(error => setOverallStatus({ type: 'error', message: `Errore download ZIP Task ${index + 1}: ${error.message || 'Errore sconosciuto'}` }))
                                  .finally(() => setLoading(false));
                              }}
                              className={`flex items-center text-sm font-medium rounded-lg py-2 px-4 ${buttonPrimaryClasses} transition-all duration-200`}
                              disabled={loading}
                            >
                              <FaDownload className="mr-2 h-4 w-4" />
                              Scarica ZIP ({job.generatedOutputFiles.length} file)
                            </button>
                          )}
                        </div>
                      )}

                      {/* Enhanced file list for single result */}
                      {generationJobs.length === 1 && job.success && job.generatedOutputFiles && job.generatedOutputFiles.length > 0 && (
                        <div className="mt-4">
                          <h5 className={`text-sm font-medium ${labelColor} mb-3`}>
                            File Generati ({job.generatedOutputFiles.length})
                          </h5>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {job.generatedOutputFiles.map((fileName, fileIndex) => (
                              <div
                                key={fileIndex}
                                className={`flex items-center p-3 rounded-lg border ${theme === 'dark' ? 'bg-gray-700/30 border-gray-600/50 hover:bg-gray-600/50' : 'bg-gray-50 border-gray-200/50 hover:bg-gray-100'} transition-colors`}
                              >
                                <FaFileAlt className={`h-4 w-4 mr-2 ${textColor} opacity-60`} />
                                <span className={`text-sm ${textColor} truncate`} title={fileName}>
                                  {fileName}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Comando Eseguito per risultati multipli */}
                      {generationJobs.length > 1 && job.commandString && (
                        <details className="mt-4">
                          <summary className={`cursor-pointer ${labelColor} text-sm font-medium hover:underline`}>
                            Comando Eseguito
                          </summary>
                          <pre className={`mt-2 p-4 border ${logViewerBorder} rounded-lg text-sm leading-relaxed ${logViewerBg} ${logViewerText} max-h-48 overflow-auto whitespace-pre-wrap break-words font-mono`}>
                            {job.commandString}
                          </pre>
                        </details>
                      )}

                      {/* Output del Comando per risultati multipli */}
                      {generationJobs.length > 1 && job.commandOutput && (
                        <details open className="mt-4">
                          <summary className={`cursor-pointer ${labelColor} text-sm font-medium hover:underline`}>
                            Output del Comando
                          </summary>
                          <pre className={`mt-2 p-4 border ${logViewerBorder} rounded-lg text-sm leading-relaxed ${logViewerBg} ${logViewerText} max-h-96 overflow-auto whitespace-pre-wrap break-words font-mono`}>
                            {job.commandOutput}
                          </pre>
                        </details>
                      )}

                      {/* NUOVO: Contenuto del File di Log per risultati multipli */}
                      {generationJobs.length > 1 && job.logFileContent && (
                        <details open className="mt-4">
                          <summary className={`cursor-pointer ${labelColor} text-sm font-medium hover:underline`}>
                            Contenuto del File di Log
                          </summary>
                          <pre className={`mt-2 p-4 border ${logViewerBorder} rounded-lg text-sm leading-relaxed ${logViewerBg} ${logViewerText} max-h-96 overflow-auto whitespace-pre-wrap break-words font-mono`}>
                            {job.logFileContent}
                          </pre>
                        </details>
                      )}

                      {/* Log Dettagliato (dalla API fetchGenerationLog) - solo se diverso dal contenuto del file di log */}
                      {job.logContent && !job.loadingLog && job.logContent !== job.logFileContent && (
                        <details open className="mt-4">
                          <summary className={`cursor-pointer ${labelColor} text-sm font-medium hover:underline`}>
                            Log Dettagliato (API)
                          </summary>
                          <pre className={`mt-2 p-4 border ${logViewerBorder} rounded-lg text-sm leading-relaxed ${logViewerBg} ${logViewerText} max-h-96 overflow-auto whitespace-pre-wrap break-words font-mono`}>
                            {job.logContent}
                          </pre>
                        </details>
                      )}

                      {job.errorLog && (
                        <div className={`mt-4 p-4 text-sm text-red-600 bg-red-50 rounded-lg border-l-4 border-red-500 dark:bg-red-900/20 dark:text-red-300 dark:border-red-600`}>
                          <div className="flex items-start">
                            <FaExclamationTriangle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                            <div>{job.errorLog}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Summary section for multiple results */}
            {generationJobs.length > 1 && (
              <div className={`mt-6 pt-4 border-t ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`}>
                <div className="flex items-center justify-between">
                  <div className={`${textColor} text-sm`}>
                    <span className="font-medium">Riepilogo:</span> {generationJobs.length} task eseguiti
                  </div>
                  <div className="flex gap-4 text-sm">
                    <span className={`flex items-center ${generationJobs.filter(j => j.success).length > 0 ? 'text-green-600 dark:text-green-400' : textColor}`}>
                      <span className="w-2 h-2 rounded-full bg-green-500 mr-1"></span>
                      {generationJobs.filter(j => j.success).length} Successi
                    </span>
                    <span className={`flex items-center ${generationJobs.filter(j => !j.success).length > 0 ? 'text-red-600 dark:text-red-400' : textColor}`}>
                      <span className="w-2 h-2 rounded-full bg-red-500 mr-1"></span>
                      {generationJobs.filter(j => !j.success).length} Errori
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Confirmation Modal */}
        {showConfirmationModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50 backdrop-blur-sm">
            <div className={`relative p-8 border-2 w-full max-w-lg shadow-2xl rounded-2xl ${cardBg} transform transition-all duration-300 ease-in-out scale-100 ${(selectedSkeletonGuideItemIds.length === 0 || selectedSkeletonGuideItemIds.length >= 20) ? 'border-red-500' : cardBorder}`}>
              <div className="text-center">
                <div className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-6 ${(selectedSkeletonGuideItemIds.length === 0 || selectedSkeletonGuideItemIds.length >= 20) ? 'bg-red-100' : (theme === 'dark' ? 'bg-indigo-100' : 'bg-emerald-100')}`}>
                  <FaExclamationTriangle className={`h-8 w-8 ${(selectedSkeletonGuideItemIds.length === 0 || selectedSkeletonGuideItemIds.length >= 20) ? 'text-red-600' : (theme === 'dark' ? 'text-indigo-600' : 'text-emerald-600')}`} />
                </div>
                <h3 className={`text-xl leading-6 font-bold mb-6 ${(selectedSkeletonGuideItemIds.length === 0 || selectedSkeletonGuideItemIds.length >= 20) ? 'text-red-600' : titleColor}`}>
                  Conferma Generazione
                </h3>
                <div className="mb-8">
                  <p className={`text-md ${textColor} leading-relaxed`}>
                    {confirmationModalMessage}
                  </p>
                </div>
                <div className="flex justify-center items-center space-x-4">
                  <button
                    onClick={proceedWithGeneration}
                    className={`px-8 py-3 text-base font-semibold rounded-xl shadow-lg focus:outline-none focus:ring-4 focus:ring-offset-2 transition-all duration-200 hover:scale-105 ${(selectedSkeletonGuideItemIds.length === 0 || selectedSkeletonGuideItemIds.length >= 20) ? 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500/50' : buttonPrimaryClasses}`}
                  >
                    Conferma e Genera
                  </button>
                  <button
                    onClick={() => setShowConfirmationModal(false)}
                    className={`px-8 py-3 ${buttonSecondaryClasses} text-base font-semibold rounded-xl focus:outline-none focus:ring-4 focus:ring-offset-2 ${theme === 'dark' ? 'focus:ring-gray-500/50 focus:ring-offset-gray-800' : 'focus:ring-gray-300/50 focus:ring-offset-white'} transition-all duration-200 hover:scale-105`}
                  >
                    Annulla
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ZIP Download Modal */}
        {showZipModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full flex items-start justify-center z-50 backdrop-blur-sm pt-12">
            <div className={`relative p-8 border-2 w-full max-w-3xl shadow-2xl rounded-2xl ${cardBg} ${cardBorder}`}>
              <div className="flex items-center mb-6">
                <FaDownload className={`h-6 w-6 ${titleColor} mr-3`} />
                <h3 className={`text-xl font-bold ${titleColor}`}>
                  Download File Generati
                </h3>
              </div>
              
              <div className="max-h-96 overflow-y-auto space-y-3 mb-6">
                <div className="flex items-center mb-4 pb-3 border-b border-gray-300 dark:border-gray-600">
                  <input
                      type="checkbox"
                      id="select-all-zip"
                      className={`h-4 w-4 rounded border-gray-300 ${theme === 'dark' ? 'bg-gray-600 border-gray-500 text-indigo-500 focus:ring-indigo-400' : 'text-emerald-600 focus:ring-emerald-500'} mr-3`}
                      checked={filesForZipModal.every(f => f.selected) && filesForZipModal.length > 0}
                      onChange={(e) => handleSelectAllZipFiles(e.target.checked)}
                  />
                  <label htmlFor="select-all-zip" className={`${labelColor} font-semibold`}>
                      Seleziona/Deseleziona Tutti ({filesForZipModal.filter(f => f.selected).length} di {filesForZipModal.length})
                  </label>
              </div>
              
              {filesForZipModal.map((file, index) => (
                <div key={index} className={`flex items-center p-3 rounded-lg border transition-colors ${
                  file.selected ? 
                    (theme === 'dark' ? 'bg-indigo-900/30 border-indigo-600/50' : 'bg-emerald-50 border-emerald-300/50') :
                    (theme === 'dark' ? 'bg-gray-700/30 border-gray-600/50' : 'bg-gray-50 border-gray-200/50')
                }`}>
                  <input
                    type="checkbox"
                    id={`zip-file-${index}`}
                    checked={file.selected}
                    onChange={() => handleZipFileSelectionChange(index)}
                    className={`h-4 w-4 rounded border-gray-300 ${theme === 'dark' ? 'bg-gray-600 border-gray-500 text-indigo-500 focus:ring-indigo-400' : 'text-emerald-600 focus:ring-emerald-500'} mr-3`}
                  />
                  <label htmlFor={`zip-file-${index}`} className={`${textColor} font-medium truncate cursor-pointer flex-1`} title={file.fileName}>
                    {file.fileName}
                  </label>
                  <button
                    type="button"
                    title="Preview"
                    onClick={(e) => { e.stopPropagation(); handlePreviewFile(file); }}
                    className={`ml-2 p-1.5 rounded-lg transition-colors ${theme === 'dark' ? 'hover:bg-gray-600 text-gray-300 hover:text-indigo-300' : 'hover:bg-gray-200 text-gray-500 hover:text-emerald-600'}`}
                    disabled={previewLoading}
                  >
                    <FaEye className="h-3.5 w-3.5" />
                  </button>
                  {file.selected && (
                    <FaCheckCircle className={`h-5 w-5 ml-2 ${theme === 'dark' ? 'text-indigo-400' : 'text-emerald-500'}`} />
                  )}
                </div>
              ))}
            </div>

            {previewLoading && (
              <div className="flex items-center justify-center py-4">
                <FaSpinner className={`animate-spin h-5 w-5 ${theme === 'dark' ? 'text-indigo-400' : 'text-emerald-500'}`} />
                <span className={`ml-2 ${textColor}`}>Caricamento preview...</span>
              </div>
            )}

            {previewContent && !previewLoading && (
              <div className="fixed inset-0 z-[60] flex flex-col" onClick={() => setPreviewContent(null)}>
                <div className={`flex-1 flex flex-col ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`} onClick={(e) => e.stopPropagation()}>
                  {/* Tab-style header */}
                  <div className={`flex items-center justify-between px-5 py-2.5 border-b shrink-0 ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-300'}`}>
                    <div className={`flex items-center gap-2 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                      <FaFileAlt className="h-4 w-4 shrink-0" />
                      <span className="font-medium text-sm" title={previewContent.fileName}>
                        {previewContent.fileName}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      {previewContent.content != null && (
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(previewContent.content);
                          }}
                          className={`p-1.5 rounded-lg transition-colors ${theme === 'dark' ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200' : 'hover:bg-gray-200 text-gray-500 hover:text-gray-800'}`}
                          title="Copia tutto"
                        >
                          <FaCopy className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setPreviewContent(null)}
                        className={`p-1.5 rounded-lg transition-colors ${theme === 'dark' ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200' : 'hover:bg-gray-200 text-gray-500 hover:text-gray-800'}`}
                        title="Chiudi preview"
                      >
                        <FaTimes className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  {/* Code content */}
                  {previewContent.content != null ? (
                    <div data-allow-scroll style={{ flex: 1, overflow: 'auto', maxWidth: 'none' }}>
                      <pre style={{ padding: '1.25rem', maxWidth: 'none', whiteSpace: 'pre', fontFamily: 'monospace', fontSize: '0.875rem', lineHeight: '1.625', margin: 0 }} className={theme === 'dark' ? 'bg-gray-900 text-gray-200' : 'bg-white text-gray-800'}>
                        {previewContent.content}
                      </pre>
                    </div>
                  ) : (
                    <div className={`flex-1 flex items-center justify-center text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      {previewContent.message}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <div className="flex justify-end items-center pt-6 space-x-4 border-t border-gray-300 dark:border-gray-600">
              <button
                onClick={() => { setShowZipModal(false); setPreviewContent(null); }}
                className={`px-6 py-3 ${buttonSecondaryClasses} font-semibold rounded-xl transition-all duration-200 hover:scale-105`}
                disabled={loading}
              >
                Annulla
              </button>
              <button
                onClick={handleDownloadZip}
                disabled={loading || filesForZipModal.filter(f => f.selected).length === 0}
                className={`px-6 py-3 ${buttonPrimaryClasses} font-semibold rounded-xl disabled:opacity-50 transition-all duration-200 hover:scale-105 flex items-center`}
              >
                {loading ? 
                  <FaSpinner className="animate-spin mr-2 h-4 w-4" /> : 
                  <FaDownload className="mr-2 h-4 w-4" />
                }
                Scarica Selezionati ({filesForZipModal.filter(f => f.selected).length})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Compact Skeleton Selection Modal */}
      {showSkeletonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm p-4">
          <div className={`${cardBg} border-2 ${cardBorder} rounded-xl shadow-2xl w-full max-w-[90vw] max-h-[90vh] overflow-hidden`}>
            {/* Modal Header */}
            <div className={`px-6 py-3 border-b ${theme === 'dark' ? 'border-gray-600' : 'border-gray-200'} flex items-center justify-between`}>
              <h3 className={`text-lg font-semibold ${titleColor}`}>Seleziona Scheletro</h3>
              <button
                onClick={() => setShowSkeletonModal(false)}
                className={`p-2 rounded-lg ${buttonSecondaryClasses} transition-all duration-200 hover:scale-105`}
              >
                <FaTimes className="h-4 w-4" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 overflow-y-auto max-h-[75vh]">
              {Object.keys(filteredSkeletonCategories).length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(filteredSkeletonCategories).map(([categoryKey, category]) => (
                    <div key={categoryKey}>
                      {/* Category Header */}
                      <div className={`flex items-center mb-2 pb-1 border-b ${theme === 'dark' ? 'border-gray-600' : 'border-gray-200'}`}>
                        <span className={`font-medium text-sm ${labelColor}`}>{category.name}</span>
                        <span className={`ml-2 px-1.5 py-0.5 text-xs rounded-full ${theme === 'dark' ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                          {category.skeletons.length}
                        </span>
                      </div>
                      
                      {/* Skeletons Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 mb-3">
                        {category.skeletons.map(skeleton => (
                          <button
                            key={skeleton.path}
                            onClick={() => handleSkeletonSelection(skeleton.path)}
                            className={`flex items-center p-2 rounded-lg border text-left transition-all duration-200 hover:scale-[1.01] ${
                              selectedSkeletonPath === skeleton.path 
                                ? (theme === 'dark' ? 'bg-indigo-900/40 border-indigo-600/50' : 'bg-emerald-100/60 border-emerald-300/50')
                                : (theme === 'dark' ? 'bg-gray-700/30 border-gray-600/50 hover:bg-gray-600/50' : 'bg-gray-50 border-gray-200/50 hover:bg-gray-100')
                            }`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className={`text-xs font-medium ${textColor} truncate`} title={skeleton.fileName}>
                                {skeleton.fileName}
                              </div>
                              <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} truncate mt-0.5`} title={skeleton.path}>
                                {skeleton.category}/
                              </div>
                            </div>
                            {selectedSkeletonPath === skeleton.path && (
                              <FaCheckCircle className={`h-3 w-3 ml-1 flex-shrink-0 ${theme === 'dark' ? 'text-indigo-400' : 'text-emerald-500'}`} />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FaCode className={`mx-auto h-12 w-12 mb-4 ${textColor} opacity-50`} />
                  <p className={`${textColor} text-sm`}>Nessuno scheletro disponibile.</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className={`px-4 py-3 border-t ${theme === 'dark' ? 'border-gray-600' : 'border-gray-200'} flex justify-between items-center`}>
              <div className={`text-xs ${textColor} opacity-75`}>
                {selectedSkeletonPath ? (
                  <span>Selezionato: <span className="font-medium">{skeletons.find(s => s.path === selectedSkeletonPath)?.path.split('/').pop()}</span></span>
                ) : (
                  'Nessuna selezione'
                )}
              </div>
              <div className="flex gap-2">
                {selectedSkeletonPath && (
                  <button
                    onClick={() => handleSkeletonSelection('')}
                    className={`px-3 py-1.5 ${buttonSecondaryClasses} text-xs rounded-lg transition-all duration-200 hover:scale-105`}
                  >
                    Deseleziona
                  </button>
                )}
                <button
                  onClick={() => setShowSkeletonModal(false)}
                  className={`px-3 py-1.5 ${buttonPrimaryClasses} text-xs rounded-lg transition-all duration-200 hover:scale-105`}
                >
                  {selectedSkeletonPath ? 'Conferma' : 'Chiudi'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  </div>
);
};

export default GeneratorePage;
